import { FormField } from '@amzn/awsui-components-core';
import type { Employee } from '@amzn/stencil-react-components/employee-lookup';
import { EmployeeLookup, useEmployeeLookup } from '@amzn/stencil-react-components/employee-lookup';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { searchEmployeesFromFintechPapi, getEmployeeByLogin } from 'src/api/papi-service';
import { FintechPapiEmployee } from 'src/api/interfaces';
import { logger } from 'src/logger';

const SEARCH_DEBOUNCE_DELAY = 300;

export interface SelectedEmployee {
  login: string;
  fullName: string;
}

interface SingleEmployeeLookupProps {
  label?: string;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  value?: SelectedEmployee;
  onChange: (employee: SelectedEmployee | null) => void;
  /** Called immediately when a search result is clicked, before async PAPI validation */
  onImmediateSelect?: (employee: SelectedEmployee) => void;
  errorText?: string;
}

const mapPapiEmployeeToResult = (emp: FintechPapiEmployee): Employee => ({
  username: emp.basicInfo.login?.toLowerCase() ?? '',
  fullName: `${emp.basicInfo.firstName ?? ''} ${emp.basicInfo.lastName ?? ''}`.trim(),
  businessTitle: emp.basicInfo.businessTitle,
  city: emp.workLocation?.city || '',
  country: emp.workLocation?.country || '',
  departmentName: emp.basicInfo.costCenterName
});

const SingleEmployeeLookup: React.FC<SingleEmployeeLookupProps> = ({
  label = 'Primary Contact',
  description,
  placeholder = 'Search by name',
  disabled = false,
  value,
  onChange,
  onImmediateSelect,
  errorText
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string>();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const getSearchResults = useCallback(async (query: string): Promise<Employee[]> => {
    if (!query || query.length < 2) return [];

    const current = valueRef.current;
    if (current && (query === current.fullName || query === current.login || query.includes(current.login))) return [];

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    return new Promise((resolve) => {
      debounceTimerRef.current = setTimeout(async () => {
        try {
          const response = await searchEmployeesFromFintechPapi(query, 10);
          resolve((response.employees ?? []).map(mapPapiEmployeeToResult));
        } catch (error) {
          logger.error('Employee search error', { error });
          resolve([]);
        }
      }, SEARCH_DEBOUNCE_DELAY);
    });
  }, []);

  const handleSelection = useCallback(
    async (selected: Employee | Employee[] | undefined) => {
      if (!selected || Array.isArray(selected)) {
        onChange(null);
        return;
      }

      const login = selected.username?.toLowerCase();
      if (!login) return;

      // Fire immediate callback synchronously before async validation
      if (onImmediateSelect) {
        onImmediateSelect({ login, fullName: selected.fullName || login });
      }

      setIsValidating(true);
      setValidationError(undefined);

      try {
        const response = await getEmployeeByLogin(login);
        if (!response.employee) {
          setValidationError('User not found in directory');
          return;
        }

        const emp = response.employee;
        if (emp.basicInfo.jobStatus === 'INACTIVE' || emp.basicInfo.employeeStatus === 'INACTIVE') {
          setValidationError('User is inactive and cannot be added');
          return;
        }

        const fullName = `${emp.basicInfo.firstName ?? ''} ${emp.basicInfo.lastName ?? ''}`.trim();
        onChange({ login: emp.basicInfo.login?.toLowerCase() ?? login, fullName: fullName || login });
        setValidationError(undefined);
      } catch (error) {
        logger.error('Employee validation error', { error });
        setValidationError('Validation failed. Please try again.');
      } finally {
        setIsValidating(false);
      }
    },
    [onChange, onImmediateSelect]
  );

  const { employeeLookupProps } = useEmployeeLookup({ onQueryChange: getSearchResults });

  const displayError = errorText || validationError;

  return (
    <FormField label={label} description={description} errorText={displayError}>
      <EmployeeLookup
        {...employeeLookupProps}
        value={value ? { username: value.login, fullName: value.fullName } : undefined}
        onSelectionChange={handleSelection}
        placeholder={placeholder}
        disabled={disabled || isValidating}
        error={!!displayError}
      />
    </FormField>
  );
};

export default SingleEmployeeLookup;
